import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './App.css';

// Auth
import Login from './components/Auth/Login';
import ResetPassword from './components/Auth/ResetPassword';

// Layout
import Layout from './components/Layout/Layout';

// Pages
import AnalyticsDashboard from './components/Analytics/AnalyticsDashboard';
import Orders from './components/Orders/Orders';
import DishList from './components/Dishes/DishList';
import CreateDish from './components/Dishes/CreateDish';
import EditDish from './components/Dishes/EditDish';
import TableGrid from './components/Tables/TableGrid';
import CouponList from './components/Coupons/CouponList';
import Settings from './components/Settings/Settings';

import { forceLogout, reasonFromError, completeLogout, LOGOUT_REASONS } from './utils/auth';

// Global axios interceptor — catches every 401 and triggers a clean logout flow.
axios.interceptors.response.use(
  res => res,
  err => {
    const status = err.response?.status;
    const onLoginPage = window.location.pathname === '/login';
    const hasToken = !!localStorage.getItem('ownerToken');

    // Only treat 401 as session-ended when the user is logged in and not on /login.
    if (status === 401 && hasToken && !onLoginPage) {
      forceLogout(reasonFromError(err));
    }
    return Promise.reject(err);
  }
);

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('ownerToken');
  return token ? children : <Navigate to="/login" replace />;
};

function SessionEndedModal({ open, reason, onSignIn }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="session-ended-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="session-ended-card"
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.85, opacity: 0 }}
            transition={{ type: 'spring', damping: 22, stiffness: 280 }}
          >
            <div className="session-ended-icon">
              <i className="bi bi-shield-lock-fill"></i>
            </div>
            <h3 className="session-ended-title">Session Ended</h3>
            <p className="session-ended-message">{reason}</p>
            <button className="session-ended-btn" onClick={onSignIn}>
              <i className="bi bi-box-arrow-in-right me-2"></i>Sign In Again
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function App() {
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [sessionEnded, setSessionEnded] = useState({ open: false, reason: '' });

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3500);
  };

  // Listen for the session_ended event dispatched by forceLogout().
  useEffect(() => {
    const handler = (e) => {
      const reason = e.detail?.reason || LOGOUT_REASONS.GENERIC;
      setSessionEnded({ open: true, reason });
    };
    window.addEventListener('session_ended', handler);
    return () => window.removeEventListener('session_ended', handler);
  }, []);

  return (
    <Router>
      {/* Global notification toast */}
      <AnimatePresence>
        {notification.show && (
          <motion.div
            className={`notification-toast ${notification.type}`}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <i className={`bi ${notification.type === 'success' ? 'bi-check-circle' : 'bi-exclamation-circle'} me-2`}></i>
            {notification.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Session ended modal — appears when the active session is revoked */}
      <SessionEndedModal
        open={sessionEnded.open}
        reason={sessionEnded.reason}
        onSignIn={completeLogout}
      />

      <Routes>
        {/* Public auth routes */}
        <Route path="/login" element={<Login showNotification={showNotification} />} />
        <Route path="/reset-password" element={<ResetPassword showNotification={showNotification} />} />

        {/* Protected app routes — all share the Layout (sidebar + topbar) */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout showNotification={showNotification} />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/analytics" replace />} />
          <Route path="analytics" element={<AnalyticsDashboard />} />
          <Route path="orders" element={<Orders showNotification={showNotification} />} />
          <Route path="dishes" element={<DishList showNotification={showNotification} />} />
          <Route path="create-dish" element={<CreateDish showNotification={showNotification} />} />
          <Route path="edit-dish/:id" element={<EditDish showNotification={showNotification} />} />
          <Route path="tables" element={<TableGrid showNotification={showNotification} />} />
          <Route path="coupons" element={<CouponList showNotification={showNotification} />} />
          <Route path="settings" element={<Settings showNotification={showNotification} />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
